import { Body, Link } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { formatRelativeTime, useRelativeTimeFormatter } from '~/features/temporal';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import TimeUtils from '~/common/time-utils';
import { MixPanel } from '~/common';
import { MIX_PANEL_EVENTS } from '~/elc/features/integrations/constants';

const LAST_SYNC = I18n.t('js.admin.integration_settings.common.last_sync');
const LAST_SYNC_ATTEMPT = I18n.t('js.admin.integration_settings.common.last_sync_attempt');
const INVALID_GRANT_ERROR_MESSAGE = I18n.t('js.admin.integration_settings.salesforce.invalid_grant_error_message');
const CONNECTION_FAILED_MESSAGE = I18n.t('js.admin.integration_settings.salesforce.connection_failed');
const DURATION = I18n.t('js.admin.integration_settings.salesforce.duration');

export const SalesforceSyncStatus = ({ latestDataSync, environment, connection }) => {
  const theme = useTheme();
  const { errorType, startedAt, finishedAt, errorMessage } = latestDataSync;

  const relativeTimeFormatter = useRelativeTimeFormatter({ numeric: 'auto' });

  const formatSyncDuration = (startTimestamp, endTimestamp) => {
    const diffInMs = endTimestamp - startTimestamp;

    // If the sync duration is less than 1 second, don't display it
    if (diffInMs < 1000) {
      return null;
    }

    return `. ${DURATION}: ${TimeUtils.getTimeEstimateDisplayNumber(Math.trunc(diffInMs / 1000), true)}`;
  };

  const handleOnReAuthPress = () => {
    MixPanel.track(MIX_PANEL_EVENTS.SALESFORCE.ENABLE, { environment });
  };

  const hasError = errorType != null || errorMessage != null;

  if (hasError) {
    if (connection.updatedAt > finishedAt) {
      return (
        <Body level="md" color={theme.colors.danger[500]}>
          {LAST_SYNC_ATTEMPT}: {formatRelativeTime(finishedAt, relativeTimeFormatter)}
        </Body>
      );
    }

    if (errorType === APP_CONSTANTS.SALESFORCE.ERROR_TYPES.INVALID_ACCESS_GRANT) {
      const href = environment === 'production' ? `/auth/salesforce_v2` : `/auth/salesforcesandbox_v2`;

      return (
        <>
          <Body level="md" color={theme.colors.danger[500]}>
            {LAST_SYNC_ATTEMPT}: {formatRelativeTime(finishedAt, relativeTimeFormatter)}
          </Body>
          <Body level="md" color={theme.colors.danger[500]}>
            {INVALID_GRANT_ERROR_MESSAGE}&nbsp;
            <Link href={href} onPress={handleOnReAuthPress} target="_blank">
              Click Here to re-authenticate
            </Link>
          </Body>
        </>
      );
    }

    if (errorType === APP_CONSTANTS.SALESFORCE.ERROR_TYPES.CONNECTION_FAILED) {
      return (
        <>
          <Body level="md" color={theme.colors.danger[500]}>
            {LAST_SYNC_ATTEMPT}: {formatRelativeTime(finishedAt, relativeTimeFormatter)}
          </Body>
          <Body level="md" color={theme.colors.danger[500]}>
            {CONNECTION_FAILED_MESSAGE}&nbsp;
          </Body>
        </>
      );
    }

    return (
      <Body level="md" color={theme.colors.danger[500]}>
        {LAST_SYNC_ATTEMPT}: {formatRelativeTime(finishedAt, relativeTimeFormatter)}
        {errorMessage}
      </Body>
    );
  }

  return (
    <Body level="md">
      {LAST_SYNC}: {formatRelativeTime(finishedAt, relativeTimeFormatter)}
      {formatSyncDuration(startedAt, finishedAt)}
    </Body>
  );
};
